import {
  TreesloGridColumn,
  TreesloGridSortOrder,
  TreesloGridSortSpec,
} from "../treeslo-grid.model";

export function getColumnSortFieldName<ItemModel>(
  column: TreesloGridColumn<ItemModel>,
): string {
  return column.sortFieldName ?? column.fieldName;
}

export function serializeSortForQuery(
  sort: TreesloGridSortSpec[],
): string | undefined {
  if (!sort.length) {
    return undefined;
  }

  return sort.map((entry) => `${entry.fieldName}:${entry.order}`).join(",");
}

function normalizeSortOrder(order: unknown): TreesloGridSortOrder | null {
  if (typeof order !== "string") {
    return null;
  }

  const normalized = order.trim().toLowerCase();
  if (normalized === "asc") {
    return "asc";
  }
  if (normalized === "desc") {
    return "desc";
  }

  return null;
}

export function parseApiSortValue(
  sort: unknown,
): TreesloGridSortSpec[] {
  if (!sort) {
    return [];
  }

  if (Array.isArray(sort)) {
    return sort
      .map((entry) => {
        if (!entry || typeof entry !== "object") {
          return null;
        }

        const fieldName = (entry as TreesloGridSortSpec).fieldName;
        const order = normalizeSortOrder((entry as TreesloGridSortSpec).order);
        if (typeof fieldName !== "string" || !fieldName.trim() || !order) {
          return null;
        }

        return {
          fieldName: fieldName.trim(),
          order,
        } satisfies TreesloGridSortSpec;
      })
      .filter((entry): entry is TreesloGridSortSpec => entry !== null);
  }

  if (typeof sort !== "string" || !sort.trim()) {
    return [];
  }

  if (sort === "asc" || sort === "desc") {
    return [];
  }

  return sort
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [fieldName, order] = part.split(":");
      return {
        fieldName: fieldName?.trim() ?? "",
        order: order?.trim().toLowerCase() === "asc" ? "asc" : "desc",
      } satisfies TreesloGridSortSpec;
    })
    .filter((entry) => !!entry.fieldName);
}

export function cycleColumnSortState(
  activeSort: TreesloGridSortSpec[],
  fieldName: string,
  maxFields: number,
): TreesloGridSortSpec[] {
  const currentIndex = activeSort.findIndex(
    (entry) => entry.fieldName === fieldName,
  );

  if (currentIndex === -1) {
    const next: TreesloGridSortSpec[] = [
      ...activeSort,
      { fieldName, order: "asc" },
    ];
    return next.length > maxFields ? next.slice(next.length - maxFields) : next;
  }

  const currentOrder = activeSort[currentIndex].order;
  if (currentOrder === "asc") {
    return activeSort.map((entry, index) =>
      index === currentIndex
        ? { ...entry, order: "desc" satisfies TreesloGridSortOrder }
        : entry,
    );
  }

  // Position 0 in the normalized API sort is the GSI primary — toggle only.
  if (currentIndex === 0) {
    return activeSort.map((entry, index) =>
      index === currentIndex
        ? { ...entry, order: "asc" satisfies TreesloGridSortOrder }
        : entry,
    );
  }

  return activeSort.filter((_, index) => index !== currentIndex);
}
